/**
 * HISTORY — governed outcomes only, in the order they were recorded.
 *
 * NOTHING HERE IS A LIVE EVENT. There is no activity feed, no "Leveri is
 * reading your records", no step that appeared and disappeared. Each line is
 * something the professional record actually holds: a workpaper version
 * prepared, a conclusion committed, a person's review of it (R7), a review
 * superseded by new evidence, a record refused. If it is not in the
 * record it is not here, because a timeline of things nobody can go back and
 * check is theatre.
 *
 * The statements are the server's own sentences, and the order is the order it
 * sent them in.
 *
 * R5.1 — THE SUPERSEDED READING LIVES HERE. A resumed piece of work establishes
 * the control balance, the population total and the difference again, and the
 * Work view used to list both readings together with nothing to say which was
 * now. The current reading is on the Work page; the earlier one is here,
 * WHOLE — nothing was dropped, it was moved to where history belongs.
 */
import type { WorkFile } from "@leveri/contracts";
import { Citation, FileTitle, Marker, RuledList, Section, SectionHead, StateText, markerTone } from "../../file/index.js";
import { ReviewPosition } from "./review.js";

export function HistorySection({ work }: { work: WorkFile }) {
  return (
    <div>
      <FileTitle
        kicker="History"
        size={28}
        meta="Governed outcomes only, in the order they were recorded. Nothing here is a live event."
      >
        {work.history.length === 0 ? "Nothing has happened on this work yet" : "What happened on this work"}
      </FileTitle>

      <Section>
        {work.history.length === 0 ? (
          <p className="text-sm text-stone">
            The record holds no outcome for this work yet. Entries appear here as they are committed.
          </p>
        ) : (
          <RuledList>
            {work.history.map((entry, index) => (
              <div key={`${entry.at}-${index}`} className="flex items-start gap-3 py-3">
                <Marker tone="progress" className="mt-[7px]" />
                <div className="min-w-0">
                  <p className="text-sm text-ink">{entry.statement}</p>
                  <div className="mt-1" title={entry.at}><Citation>{entry.atText}</Citation></div>
                </div>
              </div>
            ))}
          </RuledList>
        )}
      </Section>

      {/* WHAT THIS WORK USED TO READ. Marked as superseded on every line, so
          it can never be mistaken for the current professional conclusion. */}
      {(work.superseded.established.length > 0 || work.superseded.conclusions.length > 0) && (
        <Section id="superseded">
          <SectionHead>Superseded reading</SectionHead>
          <p className="mb-3 text-[13px] text-stone">
            What this work established before it resumed. Kept in full, and no longer current.
          </p>
          <RuledList>
            {work.superseded.established.map((item) => (
              <div key={item.id} className="py-3">
                <p className="text-[15px] text-ink-soft">{item.reading ?? item.statement}</p>
                <Citation as="div" className="mt-1" title={`${item.factKind} · ${item.derivation}`}>
                  Superseded · {item.derivationLabel || item.derivation}
                </Citation>
              </div>
            ))}
            {work.superseded.conclusions.map((row) => (
              <div key={row.id} className="py-3">
                <p className="text-sm text-ink-soft">{row.questionLabel || row.questionText}</p>
                <p className="mt-1 text-[15px]">
                  <StateText tone={markerTone(row.tone)}>{row.conclusionReading ?? row.conclusionText}</StateText>
                </p>
                <Citation as="div" className="mt-1">Superseded</Citation>
                {/* R7: a review of a superseded conclusion stays here, with why it no longer covers the work. */}
                {row.review.act && <ReviewPosition row={row} />}
              </div>
            ))}
          </RuledList>
        </Section>
      )}
    </div>
  );
}
